import type { AttentionLevel, RecurrenceRule, Task } from '@/src/renderer/lib/db/models'
import type { StartValue } from '../WhenDropdown'
import type { DeadlineValue } from '../DeadlineDropdown'
import type { DurationValue } from '../TaskEntry'
import type { DetailsSectionId } from './useDetailsSections'

/**
 * Was die AUSWAHL trägt — nicht die erste Aufgabe darin.
 *
 * Bewusst ohne React-Bezug, wie `taskMenuMarks`: gerechnet wird hier, gemessen nirgends.
 */
export interface DetailsValues {
  start: StartValue | null
  duration: DurationValue | null
  deadline: DeadlineValue | null
  recurrence: RecurrenceRule | null
  attention: AttentionLevel | null
  tags: string[]
  reward: string | null
}

const EMPTY: DetailsValues = {
  start: null,
  duration: null,
  deadline: null,
  recurrence: null,
  attention: null,
  tags: [],
  reward: null,
}

const sameDate = (a: Date | null, b: Date | null) => (a?.getTime() ?? null) === (b?.getTime() ?? null)

// Trennzeichen ist ein NUL — als ESCAPE geschrieben, nicht als rohes Byte: Sonst gälte die
// Datei als binär, und ihr Diff bliebe leer. Ein Tag-Name kann ein NUL nicht enthalten, ein
// Komma oder ein Leerzeichen schon.
const sameTags = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().join('\0') === [...b].sort().join('\0')

/**
 * Die **Wiederholung** ist ein verschachteltes Objekt; ein Vergleich Feld für Feld wäre
 * eine zweite Stelle, die jede neue Regelart mitlernen müsste. `JSON.stringify` hängt zwar
 * an der Schlüsselreihenfolge — doch wenn zwei gleiche Regeln dadurch als verschieden
 * gelten, fällt das Feld auf „nicht gesetzt", und das ist die harmlose Richtung.
 */
const sameRecurrence = (a: RecurrenceRule | null, b: RecurrenceRule | null) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/**
 * **Der Wert, den ALLE tragen — sonst keiner.**
 *
 * Dieselbe Regel, die `taskMenuMarks` für die Haken des **Aufgaben-Menüs** rechnet und der
 * die **Zeichen-Leiste** folgt, eine Stufe genauer: Dort genügt „belegt", hier muss es
 * derselbe WERT sein. Ein Feld, das den Termin der ersten Aufgabe zeigt, während nur sie
 * ihn trägt, behauptet etwas Falsches — und weil „was man ändert, ändert alle" gilt,
 * schriebe der nächste Klick diese Behauptung auf die ganze Auswahl (CONTEXT.md,
 * Aufgaben-Details).
 *
 * Kein Widerspruch zur Leiste, die bei drei verschiedenen Terminen weiter kräftig steht:
 * Sie beantwortet ausdrücklich, OB etwas dasteht, nicht welcher Wert — den zeigt der
 * Bereich, und der zeigt hier nichts, weil es keinen gemeinsamen gibt.
 *
 * Bei genau einer Aufgabe sind es schlicht deren Werte.
 *
 * @example
 * const values = useMemo(() => detailsValues(tasks), [tasks])
 */
export function detailsValues(tasks: readonly Task[]): DetailsValues {
  const first = tasks[0]
  if (!first) return EMPTY

  const rest = tasks.slice(1)
  const allAgree = (agrees: (task: Task) => boolean) => rest.every(agrees)

  const startShared = first.startDate != null && allAgree(task =>
    sameDate(task.startDate, first.startDate)
    && task.startTime === first.startTime
    && task.startPeriod === first.startPeriod)

  const deadlineShared = first.dueDate != null && allAgree(task =>
    sameDate(task.dueDate, first.dueDate)
    && task.dueTime === first.dueTime
    && task.duePeriod === first.duePeriod)

  const durationShared = first.estimatedDuration != null && allAgree(task =>
    task.estimatedDuration === first.estimatedDuration
    && (task.durationMode ?? 'duration') === (first.durationMode ?? 'duration'))

  return {
    start: startShared
      ? { date: first.startDate!, time: first.startTime ?? null, period: first.startPeriod ?? null }
      : null,
    duration: durationShared
      ? { minutes: first.estimatedDuration!, mode: first.durationMode ?? 'duration' }
      : null,
    deadline: deadlineShared
      ? { date: first.dueDate!, time: first.dueTime ?? null, period: first.duePeriod ?? null }
      : null,
    recurrence: first.recurrence != null && allAgree(task => sameRecurrence(task.recurrence, first.recurrence))
      ? first.recurrence
      : null,
    attention: first.attention != null && allAgree(task => task.attention === first.attention)
      ? first.attention
      : null,
    // Leer ist bei Tags kein „nicht gesetzt", sondern ein Wert: Wer sich nicht einig ist,
    // bekommt hier dieselbe leere Liste — und beim nächsten Klick steht genau ein Tag da.
    tags: allAgree(task => sameTags(task.tags ?? [], first.tags ?? []))
      ? [...(first.tags ?? [])]
      : [],
    reward: first.rewardId != null && allAgree(task => task.rewardId === first.rewardId)
      ? first.rewardId
      : null,
  }
}

/**
 * **Der Fingerabdruck eines Bereichs — sein Wert, als Zeichenkette.**
 *
 * Der `RewardPicker` setzt seinen sichtbaren Zustand EINMAL beim Aufbau aus `value` und
 * liest ihn danach nie wieder; `TagDropdown` tut dasselbe mit seiner Häkchenliste. Sie
 * sind als kurzlebige Popover gebaut — in den Aufgaben-Details leben sie so lange wie
 * die Spalte.
 *
 * **Wann, Deadline (Etappe 2) und der Wiederkehr-Wähler (Etappe 4) stehen NICHT mehr
 * hier.** Sie schreiben jede Änderung sofort (ADR 0026) — jede eigene Änderung wäre damit
 * ein neuer Fingerabdruck, und der Neuaufbau risse den Caret mitten im Tippen aus
 * dem Zeit- oder Zahlenfeld. Sie übernehmen einen Wert von außen selbst
 * (`useDateTimeDropdownState`, `RecurrenceDropdown`); neu aufgebaut werden sie nur noch
 * beim Wechsel der Auswahl. Beim Wiederkehr-Wähler kommt dazu, dass der erste Termin das
 * Startdatum schreibt, das Teil seines Fingerabdrucks war — und dass `DetailsSection`
 * sich über die Griffe hinweg merken muss, ob das Wann der Regel gehört.
 *
 * Ändert sich ein Wert von außen, muss der Bereich deshalb NEU AUFGEBAUT werden, sonst
 * zeigt er weiter den alten und schreibt ihn beim nächsten **Bestätigen** zurück. Von
 * außen heißt: das Kreuz im Kopf des Bereichs, eine **Marke** an der Zeile, das
 * **Aufgaben-Menü**, ein anderer Anker, eine erweiterte Auswahl.
 *
 * Der Wert ist dafür der genauere Schlüssel als die Auswahl: Er deckt all das ab, UND er
 * lässt den Bereich stehen, wo zwei Aufgaben sich einig sind — wer mit den Pfeiltasten
 * durch eine Liste ohne Termine läuft, baut die Kalender nicht bei jeder Zeile neu auf.
 *
 * Die Text-Bereiche stehen bewusst NICHT hier: Ihr Feld hängt am Entwurf, und ein
 * Fingerabdruck aus dem Getippten risse die Eingabe unter dem Caret weg. Sie
 * hängen an der Auswahl.
 *
 * @example
 * const fingerprints = useMemo(() => detailsFingerprints(values), [values])
 * const key = fingerprints[section.id] ?? selectionKey
 */
export function detailsFingerprints(
  values: DetailsValues,
): Partial<Record<DetailsSectionId, string>> {
  return {
    duration: values.duration ? `${values.duration.minutes}|${values.duration.mode}` : '-',
    attention: values.attention ?? '-',
    tags: JSON.stringify([...values.tags].sort()),
    reward: values.reward ?? '-',
  }
}
